import useRootStore from '@/modules/people/store/root';
import { defineStore } from 'pinia';
import employeeGetWithFilters from '@/lib/request/webapp/people/employee-get-with-filters';
import employeeSave, {
  EmployeeSave,
} from '@/lib/request/webapp/people/employee-save';
import getPayRates, {
  GetPayRatesResponse,
  GetPayRatesResponseContract,
} from '@/lib/request/webapp/people/get-pay-rates';
import { MAX_EMPLOYEE_LIST } from '@/modules/people/definitions';
import employeesGet, {
  EmployeesGetResponse,
  SearchFilters,
} from '@webapp-next/lib/request/webapp/people/employees-get';
import Employee, {
  INVITATION_STATUS,
  EmployeeContract,
  EmployeeLeaveAccrual,
  EmployeeAgreement,
  EmployeeAddress,
  InvitationStatus,
  EmployeeAgreementContractPayRule,
} from '@/modules/people/types/employee';
import {
  Training,
  Role,
  Workplace,
  LeaveRule,
} from '@/modules/people/types/people-setup';
import { PayRule } from '@/modules/people/types/employment';
import {
  AreaBasePayRuleOverrideMapByAreaId,
  PayRuleOverrideMapByAgreement,
  createPayRuleOverridesMap,
  PayRuleOverrideMap,
} from '@/modules/people/modules/pay-rates/utils/pay-rule';
import { Country } from '@/types/store';
import { WorkDetailsEndEmployment } from './work-detail';
import { fromJSDateToISOString } from '@/modules/people/utils/date-converter';
import { ddLogInfo, ddLogError } from '@webapp-next/lib/utils/dd-log';

/**
 * Adds additional properties with capitalized names to the payRules array within each contract object.
 * The original property names are preserved.
 *
 * @param {Object} data - The original data object.
 * @return {Object} - A new object with the added properties.
 */
function addAdditionalProperties(
  data: GetPayRatesResponse
): GetPayRatesResponse {
  const newData = { ...data }; // Create a shallow copy of the original data

  newData?.contracts?.forEach(contract => {
    contract.payRules.forEach(payRule => {
      const updatedPayRule = { ...payRule }; // Create a shallow copy of the payRule object

      Object.keys(updatedPayRule).forEach(key => {
        const capitalizedKey = key.charAt(0).toUpperCase() + key.slice(1); // Capitalize the first letter of the key

        // TODO: review this code as it adds and removes properties
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error: TODO: FEC-15 silencing TS error for now
        updatedPayRule[capitalizedKey] = updatedPayRule[key]; // Create the new capitalized property
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error: TODO: FEC-15 silencing TS error for now
        delete updatedPayRule[key]; // Remove the old property with lowercase key
      });

      contract.payRules[contract.payRules.indexOf(payRule)] = updatedPayRule; // Replace the original payRule with the updated one
    });
  });

  return newData;
}

type PeopleStatePeopleIndexed = Record<number, Partial<Employee>>;

export type PeopleState = {
  people: Partial<Employee>[]; // people list
  allFilteredPeople: Partial<Employee>[]; // people list
  roles: Role[];
  trainings: Training[];
  employee: Partial<Employee>; // active employee
  peopleIndexed: PeopleStatePeopleIndexed; // people list indexed
  searchQuery: string;
  searchResults: Partial<Employee>[];
  peopleTableSelectedRows: Partial<Employee>[];
  isFetchingEmployees: boolean;
  isAwardDisclaimerAccepted: boolean;
  customPayRates: Partial<GetPayRatesResponse>;
  mainPayDetail: Record<string, unknown>; // TODO: whats the content of this prop?
  initialBaseRate: number;
  currentPage: number;
  totalPeopleCount: number;
  installationCountry: Country;
  teamMemberTabLoadStartTime: number;
  teamMemberTabLoadEndTime: number;
};

type EmployeeAddressCache = {
  address: EmployeeAddress | undefined;
  emergency: EmployeeAddress | undefined;
};

/**
 * TODO: implement this abort controller to the request Promise
 const controller = new AbortController();

 AxiosInstance.get('', {
 signal: controller.signal
 }).then(console.log).catch(console.error);

 controller.abort();
 */
// TODO: AbortController signal + queue functionality should be baked into our fetch wrapper
const fetchEmployeesRequests: {
  latest: number;
  requests: Record<
    number,
    {
      searchOptions: SearchFilters;
      abortController: AbortController;
      request: EmployeesGetResponse;
    }
  >;
} = {
  latest: 0,
  requests: {},
};

const usePeopleStore = defineStore('webapp:people', {
  state: (): PeopleState => {
    return {
      people: [],
      allFilteredPeople: [],
      roles: [],
      trainings: [],
      employee: {},
      peopleIndexed: {},
      searchQuery: '',
      searchResults: [],
      peopleTableSelectedRows: [],
      isFetchingEmployees: false,
      isAwardDisclaimerAccepted: false,
      customPayRates: {},
      mainPayDetail: {},
      initialBaseRate: 0,
      currentPage: 0,
      totalPeopleCount: 0,
      installationCountry: {} as Country,
      teamMemberTabLoadStartTime: 0,
      teamMemberTabLoadEndTime: 0,
    };
  },
  getters: {
    // TODO: is this required to be a computed prop? or hook this with setEmployee function
    canEmployeeLogin(state: PeopleState): boolean {
      return !!(
        state.employee.Status !== undefined &&
        state.employee.Active &&
        !([INVITATION_STATUS.NOT_INVITED, INVITATION_STATUS.PENDING] as Array<
          InvitationStatus
        >).includes(state.employee.Status)
      );
    },
    payRuleOverridesMapByAgreement(
      state: PeopleState
    ): PayRuleOverrideMapByAgreement {
      return (
        state.employee.Agreements?.reduce<PayRuleOverrideMapByAgreement>(
          (current, agreement) => {
            current[agreement.Id] = createPayRuleOverridesMap(
              agreement?.ContractObject?.PayRuleOverrides || []
            );
            return current;
          },
          {}
        ) || {}
      );
    },
    areaBasePayRuleOverridesMapByAreaId(
      state: PeopleState
    ): AreaBasePayRuleOverrideMapByAreaId {
      return (
        state.customPayRates?.areaRates?.reduce<
          AreaBasePayRuleOverrideMapByAreaId
        >((current, areaRate) => {
          areaRate.areaIds.forEach(areaId => {
            current[areaId] = createPayRuleOverridesMap(
              areaRate.payRuleOverrides
            );
          });
          return current;
        }, {}) || {}
      );
    },
  },
  actions: {
    getCustomAreaBasedPayRates(): Partial<GetPayRatesResponse> {
      return this.customPayRates || {};
    },
    getEmployeePayRuleOverridesMap(
      agreementId: number
    ): PayRuleOverrideMap | undefined {
      return this.payRuleOverridesMapByAgreement?.[agreementId];
    },
    getAreaBasedPayRuleOverridesMap(
      areaId: number
    ): PayRuleOverrideMap | undefined {
      return this.areaBasePayRuleOverridesMapByAreaId?.[areaId];
    },
    getCustomAreaBasedPayRatesContracts(): GetPayRatesResponseContract[] {
      return this.customPayRates.contracts || [];
    },
    setCustomAreaBasedPayRates(payRates: GetPayRatesResponse): void {
      this.customPayRates = addAdditionalProperties(payRates);
    },
    isPayPolicyByContractId(id: number | string): boolean {
      const rootState = useRootStore();
      const term = rootState.getContractById(id);
      return term?.IsPayPolicy || false;
    },
    isPayPolicy(id: number | string): boolean {
      return this.isPayPolicyByContractId(id);
    },
    async fetchEmployeeById(
      employeeId: number,
      effectiveDate?: string
    ): Promise<void> {
      const rootState = useRootStore();

      try {
        // TODO: review this solution why we need to use this? instead of partial update the store cache
        const { data } = await employeeGetWithFilters({
          employeeId,
          effectiveDate,
        });
        this.setEmployeeBaseRates(data);
        this.setEmployee(data);
        this.setPersonIndexed(employeeId, data);

        if (data.Contract) {
          const found = rootState
            .getEmploymentTerms()
            .find(item => item.Id == data.Contract?.Id);

          if (!found) {
            rootState.setup.EmploymentTermArray.push(data.Contract);
          }
        }

        this.upsertSearchResults(data);
      } catch (e) {
        console.error('Error updating global state', e);
      }
    },
    async saveEmployee(employee: EmployeeSave): Promise<void> {
      const rootState = useRootStore();

      // TODO: add basic validations local before hitting the api
      const { data, success, message } = await employeeSave(employee);

      if (!success) {
        console.error('employeeSaveError', message);
        throw new Error(message);
      }

      await rootState.updateLeaveRules(); // TODO: check this api request as it doubles getting the employee using Mushodo and this one does the same fetchEmployeeById(data?.Id)
    },
    async fetchPayRates(employeeId: number): Promise<void> {
      const payRates = await getPayRates(employeeId);
      this.setCustomAreaBasedPayRates(payRates);
    },
    setEmployeeBaseRates(employee: Partial<Employee>): EmployeeAgreement[] {
      if (
        employee.Contract?.PayRuleArray !== undefined &&
        (employee.Contract?.Levels || []).length > 0
      ) {
        const basePayRule = (employee.Contract.PayRuleArray as Array<
          Partial<EmployeeAgreementContractPayRule> | Partial<PayRule>
        >).find(payRule => payRule.Id === employee.Contract?.BasePayRule);

        const firstAgreementBaseRate = employee.Agreements?.[0]?.BaseRate;

        if (basePayRule && firstAgreementBaseRate === null) {
          employee.Contract.DefaultBaseRate = basePayRule.HourlyRate;
        }
      }
      return (
        employee.Agreements?.map(agreement => {
          const levels = agreement.ContractObject?.Levels || [];
          if (
            (agreement.BaseRate || levels.length > 0) &&
            agreement.ContractObject?.PayRuleArray !== undefined
          ) {
            const basePayRule = (agreement.ContractObject.PayRuleArray as Array<
              Partial<EmployeeAgreementContractPayRule> | Partial<PayRule>
            >).find(
              payRule => payRule.Id === agreement.ContractObject?.BasePayRule
            );

            if (basePayRule) {
              if (agreement.BaseRate === null && levels.length > 0) {
                agreement.DefaultBaseRate = basePayRule.HourlyRate;
              }
              if (agreement.BaseRate) {
                basePayRule.HourlyRate = agreement.BaseRate;
              } else if (agreement.LevelId !== null) {
                const selectedLevel = levels.find(
                  level => level.Id === agreement.LevelId
                );
                if (selectedLevel) {
                  basePayRule.HourlyRate = selectedLevel?.BaseRate || null;
                }
              }
            }
          }

          return agreement;
        }) || []
      );
    },
    setEmployee(employee: Partial<Employee>): void {
      this.employee = employee;
    },
    getEmployeeAgreementsByIndex(index: number): EmployeeAgreement | undefined {
      return this.getEmployeeAgreements()?.[index];
    },
    getEmployee(): Partial<Employee> {
      return this.employee || {};
    },
    getEmployeeById(employeeId: number): Partial<Employee> {
      return (this.peopleIndexed || {})[employeeId];
    },
    getEmployeeId(): number {
      return this.employee.Id || 0;
    },
    getEmployeeRole(): Role {
      const rootStore = useRootStore();
      return rootStore.getRoleById(this.employee.Role);
    },
    getEmployeePosition(): Role {
      const rootStore = useRootStore();
      return rootStore.getRoleById(this.employee.Role);
    },
    getEmployeeEmploymentStatus(): number | undefined {
      return this.employee?.Contract?.EmploymentStatus;
    },
    getEmployeeAddressCache(): EmployeeAddressCache {
      return {
        address: this.employee._address,
        emergency: this.employee._emergencyAddress,
      };
    },
    getEmployeeTraining(): Training[] {
      return this.employee.TrainingArray || [];
    },
    getEmployeeStressProfile(): null | number {
      return this.employee.StressProfile || null;
    },
    getEmployeeContract(): EmployeeContract | Partial<EmployeeContract> {
      return this.employee?.Contract || {};
    },
    getEmployeeContractLeaveRules(): LeaveRule[] {
      return this.employee?.Contract?.LeaveRuleArray || [];
    },
    getEmployeeAgreements(): EmployeeAgreement[] {
      return this.employee.Agreements || [];
    },
    getEmployeeAgreementsById(
      id: number | null = null
    ): EmployeeAgreement | undefined {
      return this.getEmployeeAgreements().find(
        agreement => agreement.Id === id
      );
    },
    getEmployeeLocations(): Workplace[] {
      return this.employee.WorkplaceArray || [];
    },
    getEmployeeLocationIds(): number[] {
      return (
        this.employee?.WorkplaceArray?.map(
          (workplace: Workplace) => workplace.Company
        ) || []
      );
    },
    setEmployeeActive(active = true): void {
      if (this.employee) {
        this.employee.Active = active;
      }
    },
    setEmployeeAddressCache({
      address,
      emergency,
    }: EmployeeAddressCache): void {
      if (this.employee) {
        this.employee._address = address;
        this.employee._emergencyAddress = emergency;
      }
    },
    isSetEmployeeAddressCache(): boolean {
      return !!this.employee._address && !!this.employee._emergencyAddress;
    },
    getEmployeeDisplayName(): string | null | undefined {
      return this?.employee?.DisplayName;
    },
    setEmployeeDisplayName(displayName = ''): void {
      this.employee.DisplayName = displayName;
    },
    setEmployeeLocations(locations: Workplace[] = []): void {
      this.employee.WorkplaceArray = locations;
    },
    setEmployeeLeaveAccrual(leaveAccrual: EmployeeLeaveAccrual[] = []): void {
      this.employee.LeaveAccrualArray = leaveAccrual;
    },
    setEmployeeHourlyRate(rate = 0): void {
      this.employee.EmployeeHourlyRate = rate;
    },
    setEmployeeAgreements(agreements: EmployeeAgreement[] = []): void {
      this.employee.Agreements = agreements;
    },
    setEmployeeAgreementBaseRateById(
      agreementId: number,
      newBaseRate = 0
    ): void {
      const agreementToUpdate = this.getEmployeeAgreements().find(
        agreement => agreement.Id === agreementId
      );

      if (agreementToUpdate) {
        agreementToUpdate.BaseRate = newBaseRate;
      }
    },
    setSearchResults(searchResults: Partial<Employee>[] = []): void {
      this.searchResults = searchResults;
    },
    upsertSearchResults(employee: Employee): void {
      if (!employee) {
        return;
      }

      const indexToUpdate = this.searchResults.findIndex(
        (item: Partial<Employee>) => item.Id === employee.Id
      );

      if (indexToUpdate === -1) {
        this.searchResults.push(employee);
      } else {
        this.searchResults.splice(indexToUpdate, 1, employee);
      }
    },
    setSearchResultsByIndex(index = 0, employee: Partial<Employee>): void {
      this.searchResults.splice(index, 1, employee);
    },
    getSearchResultsByIndex(index = 0): Partial<Employee> {
      return this.searchResults[index];
    },
    setPeopleTableSelectedRows(list: Partial<Employee>[] = []): void {
      this.peopleTableSelectedRows = list;
    },
    getPeopleTableSelectedRowByIndex(index = 0): Partial<Employee> {
      return this.peopleTableSelectedRows[index];
    },
    setPeopleTableSelectedRowByIndex(
      index = 0,
      value: Partial<Employee> = {}
    ): void {
      this.peopleTableSelectedRows[index] = value;
    },
    setPeople(people: Partial<Employee>[] = []): void {
      this.people = people;
    },
    setAllFilteredPeople(people: Partial<Employee>[] = []): void {
      this.allFilteredPeople = people;
    },
    upsertPeople(employee: Employee): void {
      if (!employee) {
        return;
      }

      const indexToUpdate = this.people.findIndex(
        (item: Partial<Employee>) => item.Id === employee.Id
      );

      if (indexToUpdate === -1) {
        this.people.push(employee);
      } else {
        this.people.splice(indexToUpdate, 1, employee);
      }
    },
    setPeopleIndexed(people: PeopleStatePeopleIndexed = {}): void {
      this.peopleIndexed = people;
    },
    setPersonIndexed(employeeId: number, employee: Partial<Employee>): void {
      this.peopleIndexed[employeeId] = employee;
    },
    addEmployeeToCache(employee: Employee): void {
      this.setPersonIndexed(employee.Id, employee);
      this.upsertSearchResults(employee);
      this.upsertPeople(employee);
    },
    setTotalPeopleCount(count = 0): void {
      this.totalPeopleCount = count;
    },
    setIsAwardDisclaimerAccepted(isAccepted = false): void {
      this.isAwardDisclaimerAccepted = isAccepted;
    },
    setInitialBaseRate(rate = 0): void {
      this.initialBaseRate = rate;
    },
    getInitialBaseRate(): number {
      return this.initialBaseRate || 0;
    },
    setCurrentPage(currentPage = 0): void {
      this.currentPage = currentPage;
    },
    setSearchQuery(searchQuery = ''): void {
      this.searchQuery = searchQuery;
    },
    getSearchQuery(): string {
      return this.searchQuery?.trim() || '';
    },
    getFetchEmployeesFilters(fetchAll = false): SearchFilters {
      const rootState = useRootStore();
      const requestObj = {
        exposeMeta: true, // include all response object including headers
        max: fetchAll ? this.totalPeopleCount : MAX_EMPLOYEE_LIST,
        start: this.currentPage * MAX_EMPLOYEE_LIST,
        queryBasicData: fetchAll,
        ...rootState.getActiveFilters(),
      };

      const searchQuery = this.getSearchQuery();

      if (searchQuery.length > 0) {
        requestObj.mixSearch = searchQuery;
      }

      return requestObj;
    },
    async fetchEmployees(
      settings: {
        hideLoading?: boolean;
        refreshTable?: boolean;
        fetchAll?: boolean;
      } = {
        hideLoading: false,
        refreshTable: false,
        fetchAll: false,
      }
    ): Promise<void> {
      try {
        const config = this.getFetchEmployeesFilters(settings.fetchAll) || {
          start: 0,
        };

        let requestId = fetchEmployeesRequests.latest;

        // TODO: AbortController signal + queue functionality should be baked into our fetch wrapper
        // Create a new request id + AbortController
        requestId = new Date().getTime();
        const abortController = new AbortController();
        fetchEmployeesRequests.latest = requestId;

        // Trigger abort on all requests that are older than the current one
        Object.keys(fetchEmployeesRequests.requests).forEach(reqId => {
          const key = parseInt(reqId);
          if (key < requestId) {
            fetchEmployeesRequests.requests[key].abortController.abort();
            delete fetchEmployeesRequests.requests[requestId];
          }
        });

        if (!settings.hideLoading) {
          this.setIsFetchingEmployees(true);
        }

        // Add promise request + abortController to the queue
        fetchEmployeesRequests.requests[requestId] = {
          searchOptions: config,
          abortController,
          request: employeesGet(config, { signal: abortController.signal }),
        };

        // Add promise request to the queue
        const {
          headers = { 'x-deputy-total-count': 0 },
          data,
        } = await fetchEmployeesRequests.requests[requestId].request;

        // If we're unable to cancel the promise before it's resolved, it's going to get ignored here
        if (
          !(data instanceof Array) ||
          requestId < fetchEmployeesRequests.latest
        ) {
          delete fetchEmployeesRequests.requests[requestId];
          const error = new Error('Result ignored');
          error.name = 'ResultIgnoredError';
          throw error;
        }

        if (settings.fetchAll) {
          this.setAllFilteredPeople(
            // this code only runs if data is an array
            (data as Array<Partial<Employee>>).sort(
              (a, b) => (a.Id || 0) - (b.Id || 0)
            )
          );
          this.setIsFetchingEmployees(false);
          return;
        }

        this.setPeople(
          // this code only runs if data is an array
          (data as Array<Partial<Employee>>).map(employee => ({
            ...employee,
          }))
        );
        this.setPeopleIndexed(
          this.people.reduce(
            (
              reduced: PeopleStatePeopleIndexed,
              employee: Partial<Employee>
            ) => {
              if (employee.Id) {
                reduced[employee.Id] = employee;
              }

              return reduced;
            },
            {}
          )
        );

        this.setTotalPeopleCount(
          Number(headers ? headers['x-deputy-total-count'] : '0')
        );
        this.setSearchResults([...this.people]);
        this.setIsFetchingEmployees(false);
        this.setPeopleTableSelectedRows();
      } catch (e) {
        console.error(e);
        throw e;
      }
    },
    setIsFetchingEmployees(isFetchingEmployees = false): void {
      this.isFetchingEmployees = isFetchingEmployees;
    },
    startTeamMemberTabMeasurement(): void {
      this.teamMemberTabLoadStartTime = Math.round(performance.now());
      if (window.__APP__.EMBED.isDeputyEmbedded()) {
        const appLoadStartMessage = {
          type: 'deputy-people-load-start',
          timestamp: this.teamMemberTabLoadStartTime,
        };

        window.parent.postMessage(appLoadStartMessage, '*');
      }
    },
    finishTeamMemberTabMeasurement(): void {
      if (
        this.teamMemberTabLoadEndTime ||
        !this.teamMemberTabLoadStartTime ||
        window.__APP__.TAB_VISIBILITY_CHANGED
      ) {
        return;
      }

      // This is needed to be copied to local value to avoid race condition of
      // changing teamMemberTabLoadStartTime due to resetTeamMemberTabMeasurementTimes method,
      // which is to cover the edge case when users immediately change tab after people tab
      // starts to be loaded.
      const startTime = this.teamMemberTabLoadStartTime;

      const now = Math.round(performance.now());

      if (startTime > now) {
        const errorMessage =
          'measurement start time is invalid for team member tab';
        console.error(errorMessage);
        ddLogError(errorMessage, {
          startTime,
          timestamp: now,
        });
        return;
      }

      this.teamMemberTabLoadEndTime = now;
      const duration = now - startTime;

      const appLoadEndMessage = {
        type: 'deputy-people-load-end',
        timestamp: now,
        duration_ms: duration,
      };
      ddLogInfo('Deputy People Load End', {
        duration_ms: duration,
      });

      if (window.__APP__.EMBED.isDeputyEmbedded()) {
        window.parent.postMessage(appLoadEndMessage, '*');
      }
    },
    resetTeamMemberTabMeasurementTimes() {
      this.teamMemberTabLoadEndTime = 0;
      this.teamMemberTabLoadStartTime = 0;
    },
  },
});

export default usePeopleStore;
